import {computed, DestroyRef, effect, inject, Injectable, signal, untracked} from '@angular/core';
import {Subscription} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';

import {AppLogger, AppLoggerService} from '../../core/logging';
import {UiToastService} from '../../shared/ui';
import {ContextService} from '../context/context-signal.service';
import {HouseholdEventsService} from '../events/household-events.service';
import {ConsumptionCheckService} from './consumption-check.service';
import {
  CheckResponse,
  QuickCheck,
  QuickCheckRow,
  quantityText,
} from '../../models/consumption-check';

/**
 * Owns the household's open consumption reminders and the answers to them.
 *
 * State is signals + `computed`, matching `MealPlanStore` / `ShoppingSyncService`.
 * Three things are load-bearing here:
 *
 *   - **Answered rows stay in the list** as settled strips until the next
 *     household switch, so the list never shortens under the pointer and the
 *     user can see what their answer changed.
 *   - **The live stream is held only while a round is open.** Each subscription
 *     costs its own SSE connection, so we connect when there is something to
 *     keep in sync and drop it again once everything is answered. Events carry
 *     ids only; we refetch, matching `shopping_list.changed`.
 *   - **Loading is what creates a round** in phase 1 (the GET generates one when
 *     the household is due), so the store loads once per household rather than
 *     on every page visit.
 */
@Injectable({providedIn: 'root'})
export class ConsumptionCheckStore {
  private readonly api = inject(ConsumptionCheckService);
  private readonly context = inject(ContextService);
  private readonly events = inject(HouseholdEventsService);
  private readonly toast = inject(UiToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(ConsumptionCheckStore);

  private readonly _rows = signal<QuickCheckRow[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _startingRound = signal(false);
  private readonly _ranEmpty = signal(false);

  /** Every row in display order: open ones first loaded, settled ones in place. */
  readonly rows = this._rows.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /** A user-triggered round is in flight. */
  readonly startingRound = this._startingRound.asReadonly();

  /**
   * The last forced round came back with nothing to ask about. Worth telling
   * apart from "we haven't looked yet" — the empty state should say we looked.
   */
  readonly ranEmpty = this._ranEmpty.asReadonly();

  /** Unanswered checks — the number the nav badge and the teaser show. */
  readonly openCount = computed(() => this._rows().filter(r => r.status !== 'settled').length);

  /** True once a load has finished, so empty state and "not loaded yet" differ. */
  private readonly _loaded = signal(false);
  readonly loaded = this._loaded.asReadonly();

  /** When the round was asked — the earliest open check. */
  readonly askedAt = computed(() => earliest(this.openChecks().map(c => c.askedAt)));

  /** When the round closes — the earliest expiry among open checks. */
  readonly expiresAt = computed(() => earliest(this.openChecks().map(c => c.expiresAt)));

  private readonly openChecks = computed(() =>
    this._rows().filter(r => r.status !== 'settled').map(r => r.check),
  );

  private householdId: number | null = null;
  private streamSub: Subscription | null = null;

  constructor() {
    // Follow the active household: reload and re-point the stream on a switch.
    effect(() => {
      const householdId = this.context.context()?.household?.id ?? null;
      untracked(() => this.setHousehold(householdId));
    });

    this.destroyRef.onDestroy(() => this.unsubscribeStream());
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  private setHousehold(householdId: number | null): void {
    if (householdId === this.householdId) return;
    this.householdId = householdId;
    this._rows.set([]);
    this._loaded.set(false);
    this._error.set(null);
    this._ranEmpty.set(false);
    this.unsubscribeStream();
    if (householdId == null) return;
    this.load();
  }

  /** Refetch the open checks for the active household. */
  load(): void {
    const householdId = this.householdId;
    if (householdId == null) return;

    this._loading.set(true);
    this.api.listChecks(householdId).subscribe({
      next: checks => {
        this._rows.update(prev => mergeFromServer(prev, checks));
        this._error.set(null);
        this._loaded.set(true);
        // A round arriving any other way answers "we found nothing" for us.
        if (this.openCount() > 0) this._ranEmpty.set(false);
        this.syncStream();
      },
      error: (err: unknown) => {
        this.log.error('failed to load consumption checks', err);
        this._error.set('Quick checks are unavailable right now.');
        this._loading.set(false);
        this._loaded.set(true);
      },
      complete: () => this._loading.set(false),
    });
  }

  /**
   * Asks the server for a round right now, cadence be damned — the "Check now"
   * button. Separate from `load()` on purpose: the GET only generates a round
   * when the household is due, so nothing else in this store can produce
   * questions on demand.
   *
   * An empty answer is a real answer, not a failure: forcing skips the
   * schedule, not the bar an item has to clear to be worth asking about. We
   * record that in `ranEmpty` so the empty state can say we looked.
   */
  runRound(): void {
    const householdId = this.householdId;
    if (householdId == null || this._startingRound()) return;

    this._startingRound.set(true);
    this._ranEmpty.set(false);
    this.api.runRound(householdId).subscribe({
      next: checks => {
        this._rows.update(prev => mergeFromServer(prev, checks));
        this._error.set(null);
        this._loaded.set(true);
        this._ranEmpty.set(this.openCount() === 0);
        this.syncStream();
      },
      error: (err: unknown) => {
        this.log.error('failed to run a consumption round', err);
        this.toast.error('Could not start a check right now. Try again.');
        this._startingRound.set(false);
      },
      complete: () => this._startingRound.set(false),
    });
  }

  // ── Answering ─────────────────────────────────────────────────────────────

  /**
   * Answer one check. The row shows a saving state, then settles in place with
   * a line describing what changed. On failure the row returns to `open` — we
   * never leave a stock mutation implied that the server did not make.
   */
  respond(check: QuickCheck, response: CheckResponse, quantityLeft?: number): void {
    if (response === 'partial' && (quantityLeft == null || !isFinite(quantityLeft))) {
      this.toast.error('Enter how much is left first.');
      return;
    }

    // Preview rows exist only in this tab; answering one must not reach the API.
    if (isPreview(check)) {
      this.settle(check.id, response, quantityLeft == null ? '' : previewQuantityText(check, quantityLeft));
      return;
    }

    this.setStatus(check.id, 'saving');

    this.api.respond(check.id, response, {quantityLeft}).subscribe({
      next: result => {
        this.settle(check.id, response, result.stock ? quantityText(result.stock) : '');
      },
      error: (err: unknown) => {
        const status = err instanceof HttpErrorResponse ? err.status : 0;
        if (status === 409) {
          // Someone else answered it, or it expired while the page was open.
          // Either way the question is gone — reflect that instead of retrying.
          this.settle(check.id, response, '', 'Already answered somewhere else');
          this.load();
          return;
        }
        this.log.error(`failed to answer check ${check.id}`, err);
        this.setStatus(check.id, 'open');
        this.toast.error('That answer did not go through. Try again.');
      },
    });
  }

  /**
   * Answer every open check with `still_have` — the round was wrong. There is
   * deliberately no bulk `used_up`: removing stock stays one click per item.
   */
  respondAllStillHave(): void {
    for (const row of this._rows()) {
      if (row.status === 'open') this.respond(row.check, 'still_have');
    }
  }

  /**
   * Seeds a preview round in this tab only — two plausible questions, so the
   * screen behind a reminder can be walked through before the backend can ask
   * anything. Answering a preview row changes nothing anywhere; it settles
   * locally and disappears on the next real load.
   */
  seedPreviewRound(): void {
    const now = Date.now();
    const preview: QuickCheck[] = [
      previewCheck(-1, 'Cucumber', 'Fridge', 'piece', 6, now),
      previewCheck(-2, 'Sourdough loaf', 'Pantry', 'loaf', 5, now),
    ];

    this._rows.update(rows => [
      ...preview.map(check => ({check, status: 'open' as const})),
      // Keep anything real that is already on screen, drop stale previews.
      ...rows.filter(r => !isPreview(r.check)),
    ]);
    this._error.set(null);
    this._loaded.set(true);
    this._ranEmpty.set(false);
  }

  /** "Never ask about this product" for the whole household. */
  muteProduct(check: QuickCheck): void {
    if (isPreview(check)) {
      this.toast.info('Preview only — nothing was muted.');
      this.respond(check, 'not_now');
      return;
    }

    const householdId = this.householdId;
    const productId = check.stock?.productId;
    if (householdId == null || productId == null) return;

    const name = check.stock?.productName ?? 'this product';
    this.api.setProductOptOut(householdId, productId, true).subscribe({
      next: () => {
        this.toast.success(`We won't ask about ${name} again.`);
        // Muting doesn't answer the open question; clear it as a "not now".
        this.respond(check, 'not_now');
      },
      error: (err: unknown) => {
        this.log.error(`failed to mute product ${productId}`, err);
        this.toast.error(`Could not mute ${name}.`);
      },
    });
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private setStatus(checkId: number, status: QuickCheckRow['status']): void {
    this._rows.update(rows => rows.map(r => (r.check.id === checkId ? {...r, status} : r)));
  }

  private settle(checkId: number, response: CheckResponse, quantity: string, override?: string): void {
    this._rows.update(rows =>
      rows.map(r =>
        r.check.id === checkId
          ? {...r, status: 'settled' as const, settledText: override ?? settledText(r.check, response, quantity)}
          : r,
      ),
    );
    this.syncStream();
  }

  /**
   * Hold a live stream only while there is an open round to keep in sync —
   * every subscription costs its own SSE connection, and a household with
   * nothing to answer has nothing to hear about. A new round always arrives
   * through a load (in phase 1 the GET is what generates one), which
   * re-evaluates this.
   */
  private syncStream(): void {
    const householdId = this.householdId;
    const wanted = householdId != null && this.openCount() > 0;
    if (wanted && !this.streamSub) this.subscribeToStream(householdId!);
    if (!wanted && this.streamSub) this.unsubscribeStream();
  }

  private subscribeToStream(householdId: number): void {
    this.unsubscribeStream();
    this.streamSub = this.events.connect(householdId).subscribe(evt => {
      // `open` fires on every (re)connect — refetch to close any gap.
      if (evt.event === 'open' || evt.event.startsWith('consumption_check.')) {
        this.load();
      }
    });
  }

  private unsubscribeStream(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = null;
  }
}

/**
 * Fold a server response into the current rows: settled rows keep their place
 * so their strips survive a live refetch, open rows are replaced wholesale, and
 * anything new lands at the end.
 */
function mergeFromServer(prev: QuickCheckRow[], checks: QuickCheck[]): QuickCheckRow[] {
  const byId = new Map(checks.map(c => [c.id, c]));
  const kept: QuickCheckRow[] = [];
  const seen = new Set<number>();

  for (const row of prev) {
    if (row.status === 'settled') {
      kept.push(row);
      seen.add(row.check.id);
      continue;
    }
    const fresh = byId.get(row.check.id);
    if (!fresh) continue; // answered elsewhere or expired — drop it
    // Keep an in-flight row in its saving state; the response will settle it.
    kept.push({...row, check: fresh});
    seen.add(row.check.id);
  }

  for (const check of checks) {
    if (!seen.has(check.id)) kept.push({check, status: 'open'});
  }

  return kept;
}

/** What the settled strip says, in terms of what actually changed. */
function settledText(check: QuickCheck, response: CheckResponse, quantity: string): string {
  const where = check.stock?.storeName;
  switch (response) {
    case 'used_up':
      return where ? `Removed from ${where}` : 'Removed from stock';
    case 'still_have':
      return 'Kept — we won\'t ask again for two weeks';
    case 'partial':
      return quantity ? `Updated to ${quantity}` : 'Quantity updated';
    case 'not_now':
      return 'We\'ll ask again in a few days';
  }
}

/**
 * Preview rows carry negative ids. Real check ids come from a BIGSERIAL and are
 * always positive, so the sign is a marker no server value can collide with.
 */
function isPreview(check: QuickCheck): boolean {
  return check.id < 0;
}

function previewCheck(
  id: number,
  productName: string,
  storeName: string,
  unit: string,
  daysInStock: number,
  now: number,
): QuickCheck {
  const day = 86_400_000;
  return {
    id,
    state: 'open',
    askedAt: new Date(now),
    expiresAt: new Date(now + 7 * day),
    question: `Have you used up the ${productName.toLowerCase()}?`,
    stock: {
      id,
      productId: id,
      productName,
      storeName,
      unit,
      quantityLeft: 1,
      quantityFull: 1,
      addedAt: new Date(now - daysInStock * day),
    },
    responses: ['used_up', 'still_have', 'partial', 'not_now'],
  };
}

/** The settled line for a preview "some left", using the drafted quantity. */
function previewQuantityText(check: QuickCheck, quantityLeft: number): string {
  return quantityText({...check.stock!, quantityLeft});
}

function earliest(dates: Date[]): Date | null {
  let out: Date | null = null;
  for (const d of dates) {
    if (!d || isNaN(d.getTime())) continue;
    if (!out || d.getTime() < out.getTime()) out = d;
  }
  return out;
}
